import { useEffect, useState } from 'react'

import { ActionIcon, Collapse, Select } from '@mantine/core'
import { Check, X } from 'lucide-react'

import { useMutation } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import { isAdminOrOps } from 'src/lib/auth/permissions'
import DNButton from 'src/components/Buttons/Button/DNButton'
import StyledReadOnlyInput from 'src/components/Inputs/StyledReadOnlyInput/StyledReadOnlyInput'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_COMPANY, UPDATE_COMPANY } from 'src/graphql/companies.graphql'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconEdit from 'src/icons/IconEdit'

import FieldLabel from './FieldLabel'

interface NotificationsCardProps {
  companyData: {
    companyId: string
    atsEnabled: boolean
    recruiterLeadEmailNotifyEnabled: boolean
    sendIncomingTextNotification: boolean
  }
  initialOpened?: boolean
}

// Constants for select values
const SELECT_VALUES = {
  YES: 'yes',
  NO: 'no',
} as const

const SELECT_OPTIONS = [
  { value: SELECT_VALUES.YES, label: 'Enabled' },
  { value: SELECT_VALUES.NO, label: 'Disabled' },
] as const

// Helper functions for boolean conversion
const booleanToSelectValue = (value: boolean): string =>
  value ? SELECT_VALUES.YES : SELECT_VALUES.NO

const selectValueToBoolean = (value: string | null): boolean =>
  value === SELECT_VALUES.YES

const booleanToDisplayText = (value: boolean): string =>
  value ? 'Enabled' : 'Disabled'

const NotificationsCard = ({
  companyData,
  initialOpened = false,
}: NotificationsCardProps) => {
  const { currentUser } = useAuth()
  const isAdmin = isAdminOrOps(currentUser)
  const isRecruiter = currentUser?.roles?.includes('RECRUITER')
  const canEdit = isAdmin || isRecruiter

  const [values, setValues] = useState({ ...companyData })
  const [editing, setEditing] = useState(false)
  const [opened, setOpened] = useState(initialOpened)

  const [updateCompany, { loading }] = useMutation(UPDATE_COMPANY, {
    onCompleted: () => {
      toast('Notification settings updated successfully', 'success')
      setEditing(false)
    },
    onError: (error) => {
      toast(`Failed to update settings: ${error.message}`, 'error')
    },
    refetchQueries: [
      {
        query: GET_COMPANY,
        variables: { id: values.companyId },
      },
    ],
    awaitRefetchQueries: true,
  })

  // Sync internal state with parent data changes only when not editing
  useEffect(() => {
    if (!editing) {
      setValues({ ...companyData })
    }
  }, [companyData, editing])

  const handleEdit = () => {
    setEditing(true)
  }

  const handleCancel = () => {
    setValues({ ...companyData })
    setEditing(false)
  }

  const handleSave = async () => {
    const { companyId, ...rest } = values

    await updateCompany({
      variables: {
        id: companyId,
        input: rest,
      },
    })
  }

  const applicationConfirmationEmailLabel = (
    <FieldLabel
      label="Application Confirmation Email"
      tooltip="Sends a confirmation email to applicants when they apply to a job listing, including job details and application link"
    />
  )
  const assignedEmployeeApplicationEmailLabel = (
    <FieldLabel
      label="Assigned Employee Application Email"
      tooltip="Sends an email notification to the assigned employee when an applicant applies to a job listing, including applicant details and profile link"
    />
  )
  const applicantTextReplyEmailLabel = (
    <FieldLabel
      label="Applicant Text Reply Email"
      tooltip="Sends an email notification to the assigned employee when an applicant replies via text message, including the applicant details and their message content"
    />
  )

  return (
    <div
      className={`flex w-full flex-col gap-2 rounded-lg border border-doubleNickel-gray-200 p-3`}
    >
      <div
        className="flex w-full cursor-pointer flex-row items-center gap-2 text-sm font-semibold text-doubleNickel-gray-900"
        onClick={() => setOpened((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpened((prev) => !prev)
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={opened}
      >
        <ActionIcon variant="subtle" className="pointer-events-none">
          <IconChevronDown
            className={`h-4 w-4 ${
              !opened && '-rotate-90'
            } fill-none stroke-doubleNickel-gray-500 transition-all`}
          />
        </ActionIcon>
        Notifications
        {canEdit && !editing && (
          <DNButton
            variant="transparent"
            onClick={(e) => {
              e.stopPropagation()
              if (!opened) setOpened(true)
              handleEdit()
            }}
            className="ml-auto text-doubleNickel-gray-700"
          >
            <IconEdit className="h-3.5 w-3.5 fill-none stroke-current" />
            Edit
          </DNButton>
        )}
        {canEdit && editing && (
          <div className="ml-auto flex items-center gap-2">
            <DNButton
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleSave()
              }}
              disabled={loading}
              className="text-doubleNickel-success-500"
            >
              <Check size={14} />
              Save
            </DNButton>
            <DNButton
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleCancel()
              }}
              disabled={loading}
              className="text-doubleNickel-error-500"
            >
              <X size={14} />
              Cancel
            </DNButton>
          </div>
        )}
      </div>
      <Collapse in={opened} transitionDuration={400}>
        <div className="flex flex-col gap-2">
          <div className={`grid grid-cols-12 gap-3`}>
            {/* Application Confirmation Email */}
            <div className="col-span-3">
              {editing ? (
                <Select
                  label={applicationConfirmationEmailLabel}
                  value={booleanToSelectValue(values.atsEnabled)}
                  onChange={(value) =>
                    setValues({
                      ...values,
                      atsEnabled: selectValueToBoolean(value),
                    })
                  }
                  data={SELECT_OPTIONS}
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                />
              ) : (
                <StyledReadOnlyInput
                  label={applicationConfirmationEmailLabel}
                  value={booleanToDisplayText(values.atsEnabled)}
                />
              )}
            </div>

            {/* Assigned Employee Application Email */}
            <div className="col-span-3">
              {editing ? (
                <Select
                  label={assignedEmployeeApplicationEmailLabel}
                  value={booleanToSelectValue(
                    values.recruiterLeadEmailNotifyEnabled
                  )}
                  onChange={(value) =>
                    setValues({
                      ...values,
                      recruiterLeadEmailNotifyEnabled:
                        selectValueToBoolean(value),
                    })
                  }
                  data={SELECT_OPTIONS}
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                />
              ) : (
                <StyledReadOnlyInput
                  label={assignedEmployeeApplicationEmailLabel}
                  value={booleanToDisplayText(
                    values.recruiterLeadEmailNotifyEnabled
                  )}
                />
              )}
            </div>

            {/* Applicant Text Reply Email */}
            <div className="col-span-3">
              {editing ? (
                <Select
                  label={applicantTextReplyEmailLabel}
                  value={booleanToSelectValue(
                    values.sendIncomingTextNotification
                  )}
                  onChange={(value) =>
                    setValues({
                      ...values,
                      sendIncomingTextNotification: selectValueToBoolean(value),
                    })
                  }
                  data={SELECT_OPTIONS}
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                />
              ) : (
                <StyledReadOnlyInput
                  label={applicantTextReplyEmailLabel}
                  value={booleanToDisplayText(
                    values.sendIncomingTextNotification
                  )}
                />
              )}
            </div>
          </div>
        </div>
      </Collapse>
    </div>
  )
}

export default NotificationsCard
